import React, { useEffect, useRef, useState } from "react";
import { Icon, Message } from "semantic-ui-react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import PatternBox from "../../Patterns/PatternBox";
import Spinner from "../../../components/Spinner/Spinner";
import { Heading2 } from "../../../components/value-cssmodules/TextComponent";
import api from "../../../utils/api";
import styles from "./BestsellerSlider.module.scss";

const BestsellerSlider = () => {
    const [patterns, setPatterns] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const trackRef = useRef<HTMLDivElement>(null);
    const firstSlideRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchBestsellers = async () => {
            try {
                setLoading(true);
                const res = await api.get(
                    "/patterns?sortBy=purchaseNo&sortOrder=desc&limit=12&page=1"
                );
                const data = res.data.patterns || res.data;
                setPatterns(Array.isArray(data) ? data : []);
            } catch (err: any) {
                setError(
                    err.response?.data?.message || err.message || "Failed to load bestsellers"
                );
            } finally {
                setLoading(false);
            }
        };
        fetchBestsellers();
    }, []);

    const updateScrollState = () => {
        const track = trackRef.current;
        if (!track) return;
        setCanScrollLeft(track.scrollLeft > 0);
        setCanScrollRight(
            track.scrollLeft + track.clientWidth < track.scrollWidth - 1
        );
    };

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        updateScrollState();
        track.addEventListener("scroll", updateScrollState);
        window.addEventListener("resize", updateScrollState);
        return () => {
            track.removeEventListener("scroll", updateScrollState);
            window.removeEventListener("resize", updateScrollState);
        };
    }, [patterns]);

    const scroll = (dir: 1 | -1) => {
        const track = trackRef.current;
        if (!track) return;
        // Scroll by one slide width (including gap). use React ref instead of querySelector
        const slide = firstSlideRef.current;
        if (!slide) return;
        const gap = 16; // same as .track gap
        const slideWidth = slide.offsetWidth + gap;
        track.scrollBy({ left: dir * slideWidth, behavior: "smooth" });
    };

    if (loading) return <Spinner />;
    if (error)
        return (
            <Message negative>
                <p>{error}</p>
            </Message>
        );
    if (!patterns.length) return null;

    return (
        <section className={styles.bestsellers}>
            <header className={styles.header}>
                <Heading2>
                    <FormattedMessage
                        id="home.bestsellers"
                        defaultMessage="Bestsellers"
                    />
                </Heading2>
            </header>

            <div className={styles.sliderWrapper}>
                <button
                    className={`${styles.arrow} ${styles.arrowLeft}`}
                    onClick={() => scroll(-1)}
                    disabled={!canScrollLeft}
                    aria-label="Previous"
                >
                    <Icon name="chevron left" />
                </button>

                <div className={styles.track} ref={trackRef}>
                    {patterns.map((pattern, i) => (
                        <div
                            className={styles.slide}
                            key={pattern._id || i}
                            ref={i === 0 ? firstSlideRef : null}
                        >
                            <PatternBox pattern={pattern} />
                        </div>
                    ))}
                </div>

                <button
                    className={`${styles.arrow} ${styles.arrowRight}`}
                    onClick={() => scroll(1)}
                    disabled={!canScrollRight}
                    aria-label="Next"
                >
                    <Icon name="chevron right" />
                </button>
            </div>

            <div className={styles.footer}>
                <Link to="/patterns" className={styles.link}>
                    <button className={styles.button}>
                        <FormattedMessage
                            id="nav.invitationpbutton"
                            defaultMessage="See all patterns"
                        />
                    </button>
                </Link>
            </div>
        </section>
    );
};

export default BestsellerSlider;
